import {
	buildBulkDeleteInverse,
	buildBulkFieldRestoreInverse,
	buildBulkRemoveInverse,
	buildBulkUpdateInverse,
	buildCreateInverse,
	buildDeleteInverseFromBody,
	buildMoveInverse,
	buildPlanRestoreBody,
	buildReplaceDefaultShiftInverse,
	buildUpdateInverse,
} from "utils/undo/planAssignmentCommands";

export { buildPlanRestoreBody, buildCreateInverse, buildReplaceDefaultShiftInverse };

export function normalizePlanDay(day) {
	if (!day) {
		return null;
	}

	const value = String(day);
	return value.includes("T") ? value.split("T")[0] : value.slice(0, 10);
}

export function findPlanAssignmentId(
	planAssignments,
	{ employeeId, day, shiftId }
) {
	const targetDay = normalizePlanDay(day);
	if (!employeeId || !targetDay || shiftId == null) {
		return null;
	}

	const row = (planAssignments || []).find(
		(item) => item.employee?.id === employeeId
	);
	if (!row) {
		return null;
	}

	for (const dayPlan of row.plans || []) {
		if (normalizePlanDay(dayPlan.day) !== targetDay) {
			continue;
		}

		for (const assignment of dayPlan.assignments || []) {
			if (assignment.id == null || assignment.id === -1) {
				continue;
			}

			const assignmentShiftId = assignment.shift?.id ?? assignment.shift_id;
			if (Number(assignmentShiftId) === Number(shiftId)) {
				return assignment.id;
			}
		}
	}

	return null;
}

export function extractCreatedPlanId(response, context = {}) {
	if (!response || response.ok === false) {
		return null;
	}

	const { day, shiftId, employeeId } = context;
	const targetDay = normalizePlanDay(day);

	if (Array.isArray(response)) {
		return extractCreatedPlanId(response[0], context);
	}

	const looksLikeEmployeeRow =
		Array.isArray(response.plans) ||
		Array.isArray(response.employee?.plans) ||
		Array.isArray(response.data?.employee?.plans) ||
		Array.isArray(response.data?.plans);

	const candidates = [];

	const collectFromEmployeeRow = (employeeRow) => {
		if (!employeeRow) {
			return;
		}

		if (employeeId != null && employeeRow.id !== employeeId) {
			return;
		}

		(employeeRow.plans || []).forEach((dayPlan) => {
			if (targetDay && normalizePlanDay(dayPlan.day) !== targetDay) {
				return;
			}

			(dayPlan.assignments || []).forEach((assignment) => {
				if (assignment.id == null || assignment.id === -1) {
					return;
				}

				const assignmentShiftId =
					assignment.shift?.id ?? assignment.shift_id;

				if (shiftId != null && Number(assignmentShiftId) !== Number(shiftId)) {
					return;
				}

				candidates.push(assignment.id);
			});
		});
	};

	collectFromEmployeeRow(response.employee);
	collectFromEmployeeRow(response.data?.employee);
	if (Array.isArray(response.data?.plans)) {
		collectFromEmployeeRow(response.data);
	}
	if (Array.isArray(response.plans)) {
		collectFromEmployeeRow(response);
	}

	if (candidates.length > 0) {
		return candidates[candidates.length - 1];
	}

	if (looksLikeEmployeeRow) {
		return null;
	}

	return (
		response.id ??
		response.plan?.id ??
		response.data?.id ??
		response.assignment?.id ??
		response.assignment_id ??
		null
	);
}

export function extractCreatedPlanIdsFromResponses(responses, plansToSave = []) {
	const responseItems = Array.isArray(responses) ? responses : [responses];
	const ids = [];

	responseItems.forEach((item, index) => {
		if (item?.errors) {
			return;
		}

		const plan = plansToSave[index];
		if (!plan) {
			return;
		}

		const id = extractCreatedPlanId(item, {
			day: plan.day,
			shiftId: plan.shift_id,
			employeeId: plan.employee_id,
		});

		if (id != null) {
			ids.push(id);
		}
	});

	return ids;
}

export function extractCreatedPlanIdsFromChunkedPaste(chunks, chunkResults) {
	const ids = [];

	(chunkResults || []).forEach((result, chunkIndex) => {
		const chunkPlans = chunks[chunkIndex] || [];
		if (result?.status !== "fulfilled" || !result.value) {
			return;
		}

		const responses = Array.isArray(result.value) ? result.value : [result.value];
		responses.forEach((item, index) => {
			if (item?.errors) {
				return;
			}

			const plan = chunkPlans[index];
			if (!plan) {
				return;
			}

			const id = extractCreatedPlanId(item, {
				day: plan.day,
				shiftId: plan.shift_id,
				employeeId: plan.employee_id,
			});

			if (id != null) {
				ids.push(id);
			}
		});
	});

	return ids;
}

export function fillMissingPastedAssignmentIds(
	plansToSave,
	existingIds,
	planAssignments
) {
	const idSet = new Set(existingIds || []);
	const filled = [...(existingIds || [])];

	(plansToSave || []).forEach((plan) => {
		const id = findPlanAssignmentId(planAssignments, {
			employeeId: plan.employee_id,
			day: plan.day,
			shiftId: plan.shift_id,
		});

		if (id != null && !idSet.has(id)) {
			idSet.add(id);
			filled.push(id);
		}
	});

	return filled;
}

export function collectAssignmentFieldSnapshots(plan_assignments) {
	const snapshots = [];

	(plan_assignments || []).forEach((row) => {
		const employeeId = row.employee?.id;
		(row.plans || []).forEach((dayPlan) => {
			(dayPlan.assignments || []).forEach((assignment) => {
				if (assignment.id == null || assignment.id === -1) {
					return;
				}

				snapshots.push({
					id: assignment.id,
					employeeId,
					day: dayPlan.day,
					before: snapshotFromShiftPlan(
						assignment,
						employeeId,
						dayPlan.day
					),
				});
			});
		});
	});

	return snapshots;
}

export function collectUbicationPasteUpdates(
	plan_assignments,
	day_selection,
	filteredUbications
) {
	const updates = [];

	(day_selection || []).forEach((selectedDay) => {
		const dayPlans =
			plan_assignments[selectedDay.row].plans[selectedDay.index];
		const day = dayPlans.day;
		const employee = plan_assignments[selectedDay.row].employee;

		(dayPlans.assignments || []).forEach((shiftPlan) => {
			if (shiftPlan.id === -1) {
				return;
			}

			const before = snapshotFromShiftPlan(shiftPlan, employee.id, day);
			const ubicationIds = new Set(before.ubication_ids || []);

			filteredUbications.forEach((ubication) => {
				ubicationIds.add(ubication.id);
			});

			const after = {
				...before,
				ubication_ids: [...ubicationIds],
			};

			updates.push({
				id: shiftPlan.id,
				before,
				after,
				employeeId: employee.id,
			});
		});
	});

	return updates;
}

export function buildPlanBulkUpdateInverse(updates) {
	return buildBulkUpdateInverse(updates);
}

export function buildPlanSetFunctionsInverse(snapshots) {
	return buildBulkFieldRestoreInverse(snapshots, ["function_id"]);
}

export function buildPlanSetUbicationsInverse(snapshots) {
	return buildBulkFieldRestoreInverse(snapshots, ["ubication_ids"]);
}

export function collectSuccessfulPastePlans(plansToSave, responses) {
	const responseItems = Array.isArray(responses) ? responses : [responses];

	return (plansToSave || []).filter(
		(_, index) => !responseItems[index]?.errors
	);
}

export function collectSuccessfulPastePlansFromChunks(chunks, chunkResults) {
	const successful = [];

	(chunkResults || []).forEach((result, chunkIndex) => {
		const chunkPlans = chunks[chunkIndex] || [];
		if (result?.status !== "fulfilled" || !result.value) {
			return;
		}

		const responseItems = Array.isArray(result.value)
			? result.value
			: [result.value];

		responseItems.forEach((item, index) => {
			if (!item?.errors && chunkPlans[index]) {
				successful.push(chunkPlans[index]);
			}
		});
	});

	return successful;
}

export function snapshotFromShiftPlan(shiftPlan, employeeId, day) {
	return buildPlanRestoreBody(shiftPlan, employeeId, day) || {};
}

export function snapshotFromPlanCell(plan) {
	return snapshotFromShiftPlan(
		plan?.shift_plan,
		plan?.employee?.id,
		plan?.day
	);
}

export function snapshotFromAssignment(assignment, employeeId, day) {
	return snapshotFromShiftPlan(assignment, employeeId, day);
}

export function collectSnapshotsForDeleteIds(planAssignments, plansToDelete) {
	const ids = new Set((plansToDelete || []).map((item) => item.id));
	const snapshots = [];

	(planAssignments || []).forEach((row) => {
		(row.plans || []).forEach((dayPlan) => {
			(dayPlan.assignments || []).forEach((assignment) => {
				if (ids.has(assignment.id)) {
					snapshots.push({
						assignment,
						employeeId: row.employee?.id,
						day: dayPlan.day,
					});
				}
			});
		});
	});

	return snapshots;
}

export function buildPlanDeleteInverseFromCell(plan) {
	const employeeId = plan?.employee?.id;
	const body = buildPlanRestoreBody(plan?.shift_plan, employeeId, plan?.day);

	return buildDeleteInverseFromBody(body);
}

export function buildPlanBulkDeleteInverse(planAssignments, plansToDelete) {
	const snapshots = collectSnapshotsForDeleteIds(
		planAssignments,
		plansToDelete
	);

	return buildBulkDeleteInverse(
		snapshots.map(({ assignment, employeeId, day }) => ({
			...assignment,
			employee_id: employeeId,
			day,
		}))
	);
}

export function buildPlanMoveInverse({
	createdId,
	sourceRestoreBody,
	sourceEmployeeId,
	destinationEmployeeId,
	replacedDefaultRestoreBodies,
}) {
	return buildMoveInverse({
		createdId,
		sourceRestoreBody,
		sourceEmployeeId,
		destinationEmployeeId,
		replacedDefaultRestoreBodies,
	});
}

export function buildPlanPasteInverse(
	createdIds,
	employeeIds,
	replacedDefaultRestoreBodies = []
) {
	const ids = (createdIds || []).filter((id) => id != null && id !== -1);
	const restoreBodies = (replacedDefaultRestoreBodies || []).filter(
		(body) => body?.employee_id && body?.day && body?.shift_id
	);

	if (ids.length === 0 && restoreBodies.length === 0) {
		return null;
	}

	if (restoreBodies.length === 0) {
		return buildBulkRemoveInverse(ids, employeeIds);
	}

	if (ids.length === 1 && restoreBodies.length === 1) {
		return buildReplaceDefaultShiftInverse({
			createdId: ids[0],
			replacedDefaultRestoreBody: restoreBodies[0],
			employeeId: employeeIds?.[0],
		});
	}

	const requests = [];
	const removeInverse = buildBulkRemoveInverse(ids, employeeIds);
	if (removeInverse?.requests?.length) {
		requests.push(...removeInverse.requests);
	}

	restoreBodies.forEach((body) => {
		requests.push({
			service: "plansService",
			method: "add",
			args: [body],
		});
	});

	const allEmployeeIds = [
		...new Set(
			[...(employeeIds || []), ...restoreBodies.map((body) => body.employee_id)].filter(
				Boolean
			)
		),
	];

	return {
		action: "PASTE",
		requests,
		refresh: { employeeIds: allEmployeeIds },
	};
}

export function collectDefaultShiftRestoreBodies(
	assignments,
	defaultShiftId,
	employeeId,
	day,
	groupId
) {
	if (!defaultShiftId || !Array.isArray(assignments)) {
		return [];
	}

	return assignments
		.filter((a) => a.id !== -1 && a.shift?.id === defaultShiftId)
		.map((a) => {
			const body = buildPlanRestoreBody(a, employeeId, day);
			if (!body) {
				return null;
			}
			// Default/rest shifts are unlocked unless explicitly locked.
			body.locked = a.locked === true;
			if (groupId) {
				body.group_id = groupId;
			}
			return body;
		})
		.filter(Boolean);
}

export function buildPlanResizeInverse({
	shiftPlan,
	employee,
	originDay,
	rawIntervals,
	updatedAssignment,
}) {
	const before = snapshotFromShiftPlan(shiftPlan, employee?.id, originDay);
	const after = {
		...before,
		day: updatedAssignment.day,
		time_intervals: updatedAssignment.time_intervals,
		function_id: updatedAssignment.function_id,
		comments: updatedAssignment.comments,
		ubication_ids: updatedAssignment.ubication_ids,
		locked: updatedAssignment.locked,
	};

	return buildUpdateInverse({
		id: shiftPlan.id,
		before,
		after,
		employeeId: employee?.id,
	});
}

export {
	buildDeleteInverseFromBody,
	buildBulkRemoveInverse,
	buildBulkUpdateInverse,
	buildBulkFieldRestoreInverse,
	buildUpdateInverse,
	buildBulkDeleteInverse,
};
